// =============================================================================
// Blog — ForHim.ForHer storefront JS
// Talks to the app-proxy JSON API (/apps/blog/api/...) — same architecture as
// the `stories` app's storefront: native theme page shell + client-side fetch,
// not server-rendered Liquid for the post content itself.
// =============================================================================

(function () {
  "use strict";

  var API_BASE = "/apps/blog/api";

  var ICON_PERSON  = '<svg class="fhs-blog-icon" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4"></circle><path d="M4 20c0-4 4-6 8-6s8 2 8 6"></path></svg>';
  var ICON_EYE     = '<svg class="fhs-blog-icon" viewBox="0 0 24 24"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path><circle cx="12" cy="12" r="3"></circle></svg>';
  var ICON_COMMENT = '<svg class="fhs-blog-icon" viewBox="0 0 24 24"><path d="M4 4h16v12H8l-4 4V4z"></path></svg>';
  var ICON_HEART   = '<svg class="fhs-blog-icon" viewBox="0 0 24 24"><path d="M12 21s-7-4.5-9.5-9C.5 8 2 4 6 4c2 0 3.5 1 4.5 2.5C11.5 5 13 4 15 4c4 0 5.5 4 3.5 8-2.5 4.5-9.5 9-9.5 9z"></path></svg>';
  var ICON_SHARE   = '<svg class="fhs-blog-icon" viewBox="0 0 24 24"><circle cx="18" cy="5" r="2.5"></circle><circle cx="6" cy="12" r="2.5"></circle><circle cx="18" cy="19" r="2.5"></circle><path d="M8.2 10.8l7.6-4.6M8.2 13.2l7.6 4.6"></path></svg>';
  var ICON_IMAGE_PLACEHOLDER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><rect x="3" y="3" width="18" height="18" rx="3"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><path d="M21 15l-5-5L5 21"></path></svg>';

  function esc(str) {
    var div = document.createElement("div");
    div.textContent = str == null ? "" : String(str);
    return div.innerHTML;
  }

  function fmtDate(iso) {
    if (!iso) return "";
    var d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  }

  function initial(name) {
    return (name || "?").trim().charAt(0).toUpperCase() || "?";
  }

  // Matches the mockup's "25K", "2k" style compact counts.
  function formatK(n) {
    n = n || 0;
    if (n >= 1000) {
      var v = n / 1000;
      var rounded = v >= 10 ? Math.round(v) : Math.round(v * 10) / 10;
      return rounded + "K";
    }
    return String(n);
  }

  function dateBadge(iso) {
    if (!iso) return { day: "--", month: "" };
    var d = new Date(iso);
    return {
      day:   d.getDate(),
      month: d.toLocaleDateString(undefined, { month: "short" }),
    };
  }

  // ── Listing page ──────────────────────────────────────────────────────

  function initListing(root) {
    var feed        = root.querySelector(".fhs-blog-feed");
    var paginationEl = root.querySelector(".fhs-blog-pagination");
    var limit = 6;
    var currentPage = Math.max(1, parseInt(new URLSearchParams(window.location.search).get("blog_page") || "1", 10) || 1);
    var loading = false;

    function cardHtml(post) {
      // Prefer the post's own real Shopify Page (crawlable, unique per-post
      // URL) — falls back to the shared query-param page for any post that
      // hasn't synced yet (e.g. a sync failure), so the link never 404s.
      var href = post.shopifyPageHandle
        ? "/pages/" + encodeURIComponent(post.shopifyPageHandle)
        : root.getAttribute("data-detail-url") + "?slug=" + encodeURIComponent(post.slug);
      // Prefer a dedicated banner image for the home page card; fall back to
      // the post's card image if no banner has been set.
      var cardImgUrl = post.bannerImageUrl || post.cardImageUrl;
      var cardImgAlt = post.bannerImageUrl ? (post.bannerImageAlt || post.title) : (post.cardImageAlt || post.title);
      var img = cardImgUrl
        ? '<img src="' + esc(cardImgUrl) + '" alt="' + esc(cardImgAlt) + '" loading="lazy">'
        : "";
      var badge = dateBadge(post.publishedAt);

      return (
        '<a class="fhs-blog-card" href="' + href + '">' +
          '<div class="fhs-blog-card-media">' + img +
            '<div class="fhs-blog-date-badge">' +
              '<span class="fhs-blog-date-badge-day">' + esc(String(badge.day)) + "</span>" +
              '<span class="fhs-blog-date-badge-month">' + esc(badge.month) + "</span>" +
            "</div>" +
          "</div>" +
          '<div class="fhs-blog-card-meta">' +
            "<span>" + ICON_PERSON + esc(post.authorName) + "</span>" +
            "<span>" + ICON_EYE + formatK(post.viewCount) + " Views</span>" +
            "<span>" + ICON_COMMENT + formatK(post.commentCount) + " Comment</span>" +
            "<span>" + ICON_HEART + formatK(post.likeCount) + " Like</span>" +
            "<span>" + ICON_SHARE + formatK(post.shareCount) + " Share</span>" +
          "</div>" +
          '<h2 class="fhs-blog-card-title">' + esc(post.title) + "</h2>" +
          (post.tagline ? '<p class="fhs-blog-card-tagline">' + esc(post.tagline) + "</p>" : "") +
          '<p class="fhs-blog-card-excerpt">' + esc(post.excerpt) + "</p>" +
          '<span class="fhs-blog-card-readmore">Read More &rarr;</span>' +
        "</a>"
      );
    }

    function paginationHtml(pagination) {
      var total = pagination.totalPages;
      if (total <= 1) return "";

      var pages = [];
      var add = function (p) { if (pages.indexOf(p) === -1) pages.push(p); };
      add(1);
      add(total);
      for (var p = pagination.page - 2; p <= pagination.page + 2; p++) {
        if (p >= 1 && p <= total) add(p);
      }
      pages.sort(function (a, b) { return a - b; });

      var html = "";
      html += '<button type="button" class="fhs-blog-page-btn fhs-blog-page-prev" ' + (pagination.hasPrev ? "" : "disabled") + '>&laquo;</button>';
      var prev = null;
      pages.forEach(function (p) {
        if (prev !== null && p - prev > 1) html += '<span class="fhs-blog-page-ellipsis">&hellip;</span>';
        html += '<button type="button" class="fhs-blog-page-btn' + (p === pagination.page ? " active" : "") + '" data-page="' + p + '">' + p + "</button>";
        prev = p;
      });
      html += '<button type="button" class="fhs-blog-page-btn fhs-blog-page-next" ' + (pagination.hasNext ? "" : "disabled") + '>&raquo;</button>';
      return html;
    }

    function load(page) {
      if (loading) return;
      loading = true;
      currentPage = page;
      feed.innerHTML = '<div class="fhs-blog-loading">Loading&hellip;</div>';
      if (paginationEl) paginationEl.innerHTML = "";

      fetch(API_BASE + "/blogs?page=" + page + "&limit=" + limit)
        .then(function (r) { return r.json(); })
        .then(function (json) {
          if (!json.success) throw new Error(json.error || "Failed to load posts");
          var items = json.data.items;
          if (items.length === 0) {
            feed.innerHTML = '<div class="fhs-blog-empty">No posts yet — check back soon.</div>';
          } else {
            feed.innerHTML = items.map(cardHtml).join("");
          }
          if (paginationEl) {
            paginationEl.innerHTML = paginationHtml(json.data.pagination);
            paginationEl.querySelectorAll(".fhs-blog-page-btn[data-page]").forEach(function (btn) {
              btn.addEventListener("click", function () { goTo(parseInt(btn.getAttribute("data-page"), 10)); });
            });
            var prevBtn = paginationEl.querySelector(".fhs-blog-page-prev");
            var nextBtn = paginationEl.querySelector(".fhs-blog-page-next");
            if (prevBtn && !prevBtn.disabled) prevBtn.addEventListener("click", function () { goTo(page - 1); });
            if (nextBtn && !nextBtn.disabled) nextBtn.addEventListener("click", function () { goTo(page + 1); });
          }
          var params = new URLSearchParams(window.location.search);
          if (page > 1) params.set("blog_page", String(page)); else params.delete("blog_page");
          var qs = params.toString();
          window.history.replaceState(null, "", window.location.pathname + (qs ? "?" + qs : ""));
        })
        .catch(function (err) {
          feed.innerHTML = '<div class="fhs-blog-empty">Couldn\'t load posts. Please refresh.</div>';
          console.error("[blog]", err);
        })
        .finally(function () { loading = false; });
    }

    function goTo(page) {
      load(page);
      root.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    load(currentPage);
  }

  // ── Detail page ───────────────────────────────────────────────────────

  // Each published post also gets its own real Shopify Page at
  // /pages/<slug> (for crawlability/SEO — see the admin's Page sync). That
  // page has no ?slug= query string, so fall back to the last URL path
  // segment, which equals the post's slug (the Page's handle). The shared
  // /pages/blog-post page (no query string) still means "no post" — its
  // handle itself is not a slug.
  function getDetailSlug() {
    var fromQuery = new URLSearchParams(window.location.search).get("slug");
    if (fromQuery) return fromQuery;
    var segments = window.location.pathname.split("/").filter(Boolean);
    var last = segments[segments.length - 1];
    if (!last || last === "blog-post") return null;
    return decodeURIComponent(last);
  }

  function statsRowHtml(post, extraClass) {
    return (
      '<div class="fhs-blog-stats' + (extraClass ? " " + extraClass : "") + '">' +
        '<span class="fhs-blog-stat-btn" aria-hidden="true">' + ICON_PERSON + formatK(post.viewCount) + " Views</span>" +
        '<button type="button" class="fhs-blog-stat-btn fhs-blog-comment-count">' + ICON_COMMENT + formatK(post.commentCount) + " Comment</button>" +
        '<button type="button" class="fhs-blog-stat-btn fhs-blog-like-btn" aria-pressed="false">' +
          '<span class="fhs-blog-like-icon">♡</span> <span class="fhs-blog-like-count">' + formatK(post.likeCount) + "</span> Like" +
        "</button>" +
        '<div class="fhs-blog-share-wrapper">' +
          '<button type="button" class="fhs-blog-stat-btn fhs-blog-share-trigger">' + ICON_SHARE + "Share</button>" +
          '<div class="fhs-blog-share-menu">' +
            '<a href="#" class="fhs-blog-share-whatsapp" target="_blank" rel="noopener noreferrer">WhatsApp</a>' +
            '<a href="#" class="fhs-blog-share-facebook" target="_blank" rel="noopener noreferrer">Facebook</a>' +
            '<a href="#" class="fhs-blog-share-x" target="_blank" rel="noopener noreferrer">X (Twitter)</a>' +
            '<button type="button" class="fhs-blog-share-copy">Copy link</button>' +
          "</div>" +
        "</div>" +
      "</div>"
    );
  }

  function injectJsonLd(post, canonicalUrl, homeUrl, blogUrl) {
    var existing = document.getElementById("fhs-blog-jsonld");
    if (existing) existing.remove();

    var posting = {
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.metaDescription || post.excerpt,
      "datePublished": post.publishedAt || undefined,
      "dateModified": post.updatedAt || post.publishedAt || undefined,
      "author": { "@type": "Person", "name": post.authorName },
      "image": post.bannerImageUrl || post.cardImageUrl || undefined,
      "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl },
    };

    var breadcrumbs = {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": homeUrl },
        { "@type": "ListItem", "position": 2, "name": "Blog", "item": blogUrl },
        { "@type": "ListItem", "position": 3, "name": post.title, "item": canonicalUrl },
      ],
    };

    var data = { "@context": "https://schema.org", "@graph": [posting, breadcrumbs] };

    var script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "fhs-blog-jsonld";
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
  }

  function initDetail(root) {
    var slug = getDetailSlug();
    var customerId   = root.getAttribute("data-customer-id") || "";
    var customerName = root.getAttribute("data-customer-name") || "";
    var loginUrl      = root.getAttribute("data-login-url") || "/account/login";

    var contentEl = root.querySelector(".fhs-blog-detail-content");

    if (!slug) {
      contentEl.innerHTML = '<div class="fhs-blog-empty">No post specified.</div>';
      return;
    }

    fetch(API_BASE + "/blogs/" + encodeURIComponent(slug))
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (!json.success) throw new Error(json.error || "Post not found");
        renderPost(json.data);
      })
      .catch(function (err) {
        contentEl.innerHTML = '<div class="fhs-blog-empty">This post couldn\'t be found.</div>';
        console.error("[blog]", err);
      });

    function renderPost(post) {
      document.title = post.title;

      // Reuses the topbar's own "Back to Blog" link href rather than
      // duplicating the blog-listing URL logic here.
      var backLink = document.querySelector(".fhs-blog-back");
      var blogUrl = backLink ? backLink.getAttribute("href") : "/pages/blog";
      var blogUrlAbsolute = new URL(blogUrl, window.location.origin).href;
      injectJsonLd(post, window.location.href, window.location.origin + "/", blogUrlAbsolute);

      var authorMedia = post.authorImageUrl
        ? '<img src="' + esc(post.authorImageUrl) + '" alt="' + esc(post.authorImageAlt || post.authorName) + '">'
        : '<span class="fhs-blog-author-fallback">' + esc(initial(post.authorName)) + "</span>";

      var writtenByMedia = post.authorImageUrl
        ? '<img src="' + esc(post.authorImageUrl) + '" alt="' + esc(post.authorImageAlt || post.authorName) + '">'
        : '<span class="fhs-blog-written-by-fallback" aria-hidden="true">' + ICON_IMAGE_PLACEHOLDER + "</span>";

      var banner = post.bannerImageUrl
        ? '<div class="fhs-blog-banner"><img src="' + esc(post.bannerImageUrl) + '" alt="' + esc(post.bannerImageAlt || post.title) + '"></div>'
        : "";

      contentEl.innerHTML =
        '<h1 class="fhs-blog-detail-title">' + esc(post.title) + "</h1>" +
        (post.tagline ? '<p class="fhs-blog-detail-tagline">' + esc(post.tagline) + "</p>" : "") +
        '<div class="fhs-blog-author-row">' +
          '<div class="fhs-blog-author">' +
            authorMedia +
            "<div>" +
              '<div class="fhs-blog-author-name">' + esc(post.authorName) +
                (post.authorDesignation ? ' <span class="fhs-blog-author-designation">· ' + esc(post.authorDesignation) + "</span>" : "") +
              "</div>" +
              '<div class="fhs-blog-author-date">' + fmtDate(post.publishedAt) + "</div>" +
            "</div>" +
          "</div>" +
          statsRowHtml(post) +
        "</div>" +
        banner +
        '<div class="fhs-blog-body">' + post.body + "</div>" +
        '<div class="fhs-blog-written-by">' +
          '<p class="fhs-blog-written-by-label">Written by</p>' +
          '<div class="fhs-blog-written-by-row">' +
            writtenByMedia +
            "<div>" +
              '<p class="fhs-blog-written-by-name">' + esc(post.authorName) + "</p>" +
              (post.authorDesignation ? '<p class="fhs-blog-written-by-designation">' + esc(post.authorDesignation) + "</p>" : "") +
            "</div>" +
          "</div>" +
        "</div>" +
        statsRowHtml(post, "fhs-blog-stats-repeat") +
        '<div class="fhs-blog-comments" id="fhs-blog-comments-section">' +
          '<h3>' + ICON_COMMENT + 'Comments (<span class="fhs-blog-comment-count-heading">' + formatK(post.commentCount) + "</span>)</h3>" +
          '<div class="fhs-blog-comment-form-slot"></div>' +
          '<div class="fhs-blog-comment-list"></div>' +
        "</div>";

      // Two stats rows (top byline + bottom repeat) share markup/behavior —
      // wire both, each scoped to its own DOM subtree.
      root.querySelectorAll(".fhs-blog-stats").forEach(function (statsEl) {
        wireLike(statsEl, post);
        wireShare(statsEl, post);
      });
      root.querySelectorAll(".fhs-blog-comment-count").forEach(function (btn) {
        btn.addEventListener("click", function () {
          var target = document.getElementById("fhs-blog-comments-section");
          if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      });
      wireComments(root, post, customerId, customerName, loginUrl);
    }

    function wireLike(statsEl, post) {
      var btn = statsEl.querySelector(".fhs-blog-like-btn");
      btn.addEventListener("click", function () {
        if (!customerId) {
          window.location.href = loginUrl + "?return_url=" + encodeURIComponent(window.location.pathname + window.location.search);
          return;
        }
        btn.setAttribute("disabled", "disabled");
        fetch(API_BASE + "/blogs/" + encodeURIComponent(slug) + "/like", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ shopifyCustomerId: customerId }),
        })
          .then(function (r) { return r.json(); })
          .then(function (json) {
            if (!json.success) throw new Error(json.error);
            var liked = json.data.liked;
            root.querySelectorAll(".fhs-blog-like-btn").forEach(function (b) {
              b.classList.toggle("liked", liked);
              b.setAttribute("aria-pressed", liked ? "true" : "false");
              b.querySelector(".fhs-blog-like-icon").textContent = liked ? "♥" : "♡";
              b.querySelector(".fhs-blog-like-count").textContent = formatK(json.data.likeCount);
            });
          })
          .catch(function (err) { console.error("[blog]", err); })
          .finally(function () { btn.removeAttribute("disabled"); });
      });
    }

    function wireShare(statsEl, post) {
      var wrapper = statsEl.querySelector(".fhs-blog-share-wrapper");
      var trigger = wrapper.querySelector(".fhs-blog-share-trigger");
      var menu    = wrapper.querySelector(".fhs-blog-share-menu");
      var url     = window.location.href;

      wrapper.querySelector(".fhs-blog-share-whatsapp").href = "https://wa.me/?text=" + encodeURIComponent(post.title + " " + url);
      wrapper.querySelector(".fhs-blog-share-facebook").href = "https://www.facebook.com/sharer/sharer.php?u=" + encodeURIComponent(url);
      wrapper.querySelector(".fhs-blog-share-x").href = "https://twitter.com/intent/tweet?url=" + encodeURIComponent(url) + "&text=" + encodeURIComponent(post.title);

      function record(platform) {
        fetch(API_BASE + "/blogs/" + encodeURIComponent(slug) + "/share", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ platform: platform, shopifyCustomerId: customerId || undefined }),
        }).catch(function () {});
      }

      trigger.addEventListener("click", function (e) {
        e.stopPropagation();
        if (navigator.share) {
          navigator.share({ title: post.title, url: url }).then(function () { record("native"); }).catch(function () {});
          return;
        }
        menu.classList.toggle("open");
      });

      menu.querySelectorAll("a").forEach(function (a) {
        a.addEventListener("click", function () {
          var platform = a.className.replace("fhs-blog-share-", "");
          record(platform);
        });
      });

      wrapper.querySelector(".fhs-blog-share-copy").addEventListener("click", function () {
        var btn = this;
        var copyPromise = navigator.clipboard && navigator.clipboard.writeText
          ? navigator.clipboard.writeText(url)
          : Promise.resolve();
        copyPromise.then(function () {
          var original = btn.textContent;
          btn.textContent = "Copied!";
          record("copy_link");
          setTimeout(function () { btn.textContent = original; }, 1500);
        });
      });

      document.addEventListener("click", function (e) {
        if (!wrapper.contains(e.target)) menu.classList.remove("open");
      });
    }

    function commentItemHtml(c) {
      return (
        '<div class="fhs-blog-comment-item" data-comment-id="' + c.id + '">' +
          '<span class="fhs-blog-comment-date">' + fmtDate(c.createdAt) + "</span>" +
          '<div class="fhs-blog-comment-avatar">' + esc(initial(c.authorName)) + "</div>" +
          '<div class="fhs-blog-comment-content">' +
            '<div class="fhs-blog-comment-header">' +
              '<span class="fhs-blog-comment-author">' + esc(c.authorName) + "</span>" +
              (c.isApproved ? "" : '<span class="fhs-blog-comment-pending">Pending approval</span>') +
            "</div>" +
            '<p class="fhs-blog-comment-body">' + esc(c.body) + "</p>" +
            (c.isMine ? '<button type="button" class="fhs-blog-comment-delete">Delete</button>' : "") +
          "</div>" +
        "</div>"
      );
    }

    function wireComments(root, post, customerId, customerName, loginUrl) {
      var formSlot = root.querySelector(".fhs-blog-comment-form-slot");
      var listEl   = root.querySelector(".fhs-blog-comment-list");

      // Still requires a logged-in customer (anti-spam / moderation stays
      // intact), but the display name is editable — pre-filled from the
      // account, not locked to it, matching the design's "Your name" field.
      if (customerId) {
        formSlot.innerHTML =
          '<form class="fhs-blog-comment-form">' +
            '<input type="text" name="authorName" class="fhs-blog-comment-form-name" placeholder="Your name" maxlength="80" value="' + esc(customerName) + '" required>' +
            '<textarea name="body" placeholder="Write a comment…" maxlength="1000" required></textarea>' +
            '<div class="fhs-blog-comment-form-actions">' +
              '<span class="fhs-blog-comment-error"></span>' +
              '<button type="submit" class="fhs-blog-btn fhs-blog-btn-primary">Post Comment</button>' +
            "</div>" +
          "</form>";

        formSlot.querySelector("form").addEventListener("submit", function (e) {
          e.preventDefault();
          var form = e.target;
          var nameInput = form.querySelector('input[name="authorName"]');
          var textarea = form.querySelector("textarea");
          var errorEl = form.querySelector(".fhs-blog-comment-error");
          var submitBtn = form.querySelector("button[type=submit]");
          errorEl.textContent = "";

          var authorName = nameInput.value.trim();
          if (!authorName) { errorEl.textContent = "Please enter your name."; return; }

          submitBtn.setAttribute("disabled", "disabled");

          fetch(API_BASE + "/blogs/" + encodeURIComponent(slug) + "/comments", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ shopifyCustomerId: customerId, authorName: authorName, body: textarea.value }),
          })
            .then(function (r) { return r.json(); })
            .then(function (json) {
              if (!json.success) { errorEl.textContent = json.error || "Couldn't post comment."; return; }
              listEl.insertAdjacentHTML("afterbegin", commentItemHtml(json.data.comment));
              textarea.value = "";
              bindDeleteButtons();
            })
            .catch(function () { errorEl.textContent = "Couldn't post comment. Please try again."; })
            .finally(function () { submitBtn.removeAttribute("disabled"); });
        });
      } else {
        formSlot.innerHTML =
          '<div class="fhs-blog-comment-login">' +
            '<a href="' + loginUrl + "?return_url=" + encodeURIComponent(window.location.pathname + window.location.search) + '">Log in</a> to leave a comment.' +
          "</div>";
      }

      function bindDeleteButtons() {
        listEl.querySelectorAll(".fhs-blog-comment-delete").forEach(function (btn) {
          btn.onclick = function () {
            var item = btn.closest(".fhs-blog-comment-item");
            var commentId = item.getAttribute("data-comment-id");
            if (!confirm("Delete this comment?")) return;
            fetch(API_BASE + "/blogs/" + encodeURIComponent(slug) + "/comments/" + encodeURIComponent(commentId), {
              method: "DELETE",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ shopifyCustomerId: customerId }),
            })
              .then(function (r) { return r.json(); })
              .then(function (json) { if (json.success) item.remove(); })
              .catch(function () {});
          };
        });
      }

      var url = API_BASE + "/blogs/" + encodeURIComponent(slug) + "/comments" +
        (customerId ? "?shopifyCustomerId=" + encodeURIComponent(customerId) : "");

      fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (json) {
          if (!json.success) return;
          var comments = json.data.comments;
          if (comments.length === 0) {
            listEl.innerHTML = '<p class="fhs-blog-empty" style="padding:20px 0;">No comments yet. Be the first to share your thoughts!</p>';
          } else {
            listEl.innerHTML = comments.map(commentItemHtml).join("");
            bindDeleteButtons();
          }
        })
        .catch(function (err) { console.error("[blog]", err); });
    }
  }

  document.addEventListener("DOMContentLoaded", function () {
    var listRoot = document.querySelector(".fhs-blog-list");
    if (listRoot) initListing(listRoot);

    var detailRoot = document.querySelector(".fhs-blog-detail");
    if (detailRoot) initDetail(detailRoot);
  });
})();
